if (!customElements.get('gift-box-picker')) {
  customElements.define(
    'gift-box-picker',
    class GiftBoxPicker extends HTMLElement {
      constructor() {
        super();
        this._initialized = false;
        this._confirmedCompanion = null;
        this._confirmedPrimary = null;
        this._confirmedCombo = null;
        this._previewPair = null;
        this._giftBoxSlideInjected = false;
        this._revertType = null;
        this._miniSnapshot = null;
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.radios = this.querySelectorAll('.gift-box-picker__radio');

        this.modal = this.querySelector('[data-gift-box-modal]');
        this.doubleSummary = this.querySelector('.gift-box-picker__double-summary');
        this.summaryNameEl = this.querySelector('[data-double-summary-name]');

        this.miniModal = this.querySelector('[data-mini-modal]');
        this.miniSummary = this.querySelector('.gift-box-picker__mini-summary');
        this.miniSummaryText = this.querySelector('[data-mini-summary-text]');
        this._confirmedMini = false;

        if (this.modal) {
          this.modal.remove();
          document.body.appendChild(this.modal);
        }

        if (this.miniModal) {
          this.miniModal.remove();
          document.body.appendChild(this.miniModal);
          this.miniInputs = this.miniModal.querySelectorAll('.gift-box-picker__mini-qty');
          this.miniError = this.miniModal.querySelector('.gift-box-picker__mini-error');
          this.miniCountEl = this.miniModal.querySelector('[data-mini-current]');
        } else {
          this.miniInputs = this.querySelectorAll('.gift-box-picker__mini-qty');
          this.miniError = this.querySelector('.gift-box-picker__mini-error');
          this.miniCountEl = this.querySelector('[data-mini-current]');
        }

        this._currentType = this.selectedType;

        this._loadImageConfig();
        this._initQuantityToggle();
        this._initGalleryImage();
        this._initPageReframe();
        this._bindEvents();
        this._updatePanels();
        this._updateMiniCount();
        this._updateMiniButtons();
        this._checkUrlAutoSelect();
      }

      get selectedType() {
        const checked = this.querySelector('.gift-box-picker__radio:checked');
        return checked ? checked.value : 'none';
      }

      get selectedGiftVariantId() {
        const checked = this.querySelector('.gift-box-picker__radio:checked');
        return checked ? checked.dataset.giftVariantId : null;
      }

      get selectedCompanionVariantId() {
        if (this.selectedType === 'double') {
          return this._confirmedCompanion ? this._confirmedCompanion.variantId : null;
        }
        return null;
      }

      get selectedPrimaryVariantId() {
        if (this.selectedType === 'double' && this._confirmedPrimary) {
          return this._confirmedPrimary.variantId;
        }
        return this.dataset.currentVariantId || null;
      }

      get miniTotal() {
        let total = 0;
        this.miniInputs.forEach((input) => {
          total += parseInt(input.value, 10) || 0;
        });
        return total;
      }

      get isValid() {
        const type = this.selectedType;
        if (type === 'none') return true;
        if (type === 'single') return true;
        if (type === 'double') return !!this.selectedCompanionVariantId && !!this.selectedPrimaryVariantId;
        if (type === 'mini') return this._confirmedMini && this.miniTotal === 4;
        return true;
      }

      getGiftBoxItems() {
        const type = this.selectedType;
        if (type === 'none') return [];

        const groupId = this._generateGroupId();
        const items = [];
        const giftBoxImage = this._getActiveGiftBoxImages()[0] || '';

        if (type === 'single' || type === 'double') {
          const boxProps = { _gift_box_id: groupId, _gift_box_type: type };
          if (giftBoxImage) boxProps._gift_box_image = giftBoxImage;
          items.push({
            id: parseInt(this.selectedGiftVariantId, 10),
            quantity: 1,
            properties: boxProps,
          });
        }

        if (type === 'double' && this._confirmedCompanion) {
          items.push({
            id: parseInt(this._confirmedCompanion.variantId, 10),
            quantity: 1,
            properties: { _gift_box_id: groupId, _gift_box_role: 'companion' },
          });
        }

        if (type === 'mini') {
          const miniBoxProps = { _gift_box_id: groupId, _gift_box_type: 'mini' };
          if (giftBoxImage) miniBoxProps._gift_box_image = giftBoxImage;
          items.push({
            id: parseInt(this.selectedGiftVariantId, 10),
            quantity: 1,
            properties: miniBoxProps,
          });

          this.miniInputs.forEach((input) => {
            const qty = parseInt(input.value, 10) || 0;
            if (qty > 0) {
              items.push({
                id: parseInt(input.dataset.variantId, 10),
                quantity: qty,
                properties: { _gift_box_id: groupId, _gift_box_role: 'companion' },
              });
            }
          });
        }

        return { items, groupId };
      }

      getMainProductVariantId() {
        if (this.selectedType === 'double' && this._confirmedPrimary) {
          return parseInt(this._confirmedPrimary.variantId, 10);
        }
        return null;
      }

      getMainProductProperties(groupId) {
        const type = this.selectedType;
        if (type === 'none') return {};
        return { _gift_box_id: groupId, _gift_box_role: 'primary' };
      }

      /* ──────────── Image Config ──────────── */

      _loadImageConfig() {
        this._imageConfig = { single: null, double_default: null, mini: null, double_configs: [] };
        const script = this.querySelector('[data-gift-box-images]');
        if (!script) return;
        try {
          this._imageConfig = JSON.parse(script.textContent);
        } catch (_) {
          // keep defaults
        }
      }

      _getSingleImages() {
        const currentHandle = this.dataset.currentHandle;
        for (const cfg of this._imageConfig.single_configs || []) {
          if (cfg.handle.trim() === currentHandle) {
            return cfg.images || (cfg.image ? [cfg.image] : []);
          }
        }
        return this._imageConfig.single ? [this._imageConfig.single] : [];
      }

      _getDoubleImages(companionHandle, primaryHandleOverride) {
        const primaryHandle =
          primaryHandleOverride ||
          (this._confirmedPrimary ? this._confirmedPrimary.handle : this.dataset.currentHandle);
        const pair = [primaryHandle, companionHandle].sort();

        for (const cfg of this._imageConfig.double_configs || []) {
          const cfgPair = cfg.handles.split(',').map((h) => h.trim()).sort();
          if (cfgPair[0] === pair[0] && cfgPair[1] === pair[1]) {
            return cfg.images || (cfg.image ? [cfg.image] : []);
          }
        }
        return this._imageConfig.double_default ? [this._imageConfig.double_default] : [];
      }

      /* ──────────── Quantity toggle ──────────── */

      _initQuantityToggle() {
        const sectionId = this.dataset.sectionId;
        this._quantityForm = document.getElementById(`Quantity-Form-${sectionId}`);
      }

      _updateQuantityVisibility() {
        if (!this._quantityForm) return;
        const show = this.selectedType === 'none';
        this._quantityForm.style.display = show ? '' : 'none';
      }

      /* ──────────── Gallery gift box image ──────────── */

      _initGalleryImage() {
        const sectionId = this.dataset.sectionId;
        this._sectionId = sectionId;
        this._mediaGallery = document.querySelector(`#MediaGallery-${sectionId}`);
        if (!this._mediaGallery) return;

        const hasAnyImage =
          this._imageConfig.single ||
          this._imageConfig.double_default ||
          (this._imageConfig.double_configs && this._imageConfig.double_configs.length) ||
          (this._imageConfig.single_configs && this._imageConfig.single_configs.length);
        if (!hasAnyImage) return;

        const gallerySlider = this._mediaGallery.querySelector(`#Slider-Gallery-${sectionId}`);
        const thumbSlider = this._mediaGallery.querySelector(`#Slider-Thumbnails-${sectionId}`);
        if (!gallerySlider) return;

        this._gallerySlider = gallerySlider;
        this._thumbSlider = thumbSlider;
        this._giftBoxSlides = [];
        this._giftBoxSlideInjected = true;

        this._firstProductMediaId = null;
        const firstProductSlide = gallerySlider.querySelector('[data-media-id]:not(.product__media-item--giftbox)');
        if (firstProductSlide) {
          this._firstProductMediaId = firstProductSlide.dataset.mediaId;
        }
      }

      _injectGiftBoxSlides(imageUrls, altText) {
        this._removeGiftBoxSlides();

        const sectionId = this._sectionId;

        const reversed = [...imageUrls].reverse();
        reversed.forEach((url, ri) => {
          const originalIndex = imageUrls.length - 1 - ri;
          const mediaId = `${sectionId}-giftbox-${originalIndex}`;

          const slide = document.createElement('li');
          slide.id = `Slide-${mediaId}`;
          slide.className = 'product__media-item grid__item slider__slide product__media-item--giftbox';
          slide.dataset.mediaId = mediaId;
          slide.innerHTML = `<img
            class="product__media-img gift-box-gallery-img"
            src="${url}"
            alt="${altText}"
            width="600"
            loading="lazy"
            style="width:100%;height:auto;object-fit:contain;"
          >`;
          this._gallerySlider.prepend(slide);

          let thumb = null;
          if (this._thumbSlider) {
            thumb = document.createElement('li');
            thumb.className = 'thumbnail-list__item slider__slide thumbnail-list__item--giftbox';
            thumb.dataset.target = mediaId;
            thumb.innerHTML = `<button
              class="thumbnail global-media-settings global-media-settings--no-shadow"
              aria-label="${altText} ${originalIndex + 1}"
              aria-current="false"
              aria-controls="GalleryViewer-${sectionId}"
            ><img
              class="gift-box-thumb-img"
              src="${url}"
              alt="${altText}"
              loading="lazy"
              width="104"
              height="104"
              style="object-fit:contain;"
            ></button>`;
            this._thumbSlider.prepend(thumb);

            thumb.querySelector('button').addEventListener('click', () => {
              if (typeof this._mediaGallery.setActiveMedia === 'function') {
                this._mediaGallery.setActiveMedia(mediaId, false);
              }
            });
          }

          this._giftBoxSlides.push({ slide, thumb, mediaId });
        });

        if (this._giftBoxSlides.length > 0) {
          const firstMediaId = `${sectionId}-giftbox-0`;
          if (typeof this._mediaGallery.setActiveMedia === 'function') {
            this._mediaGallery.setActiveMedia(firstMediaId, true);
          } else {
            const firstSlide = this._giftBoxSlides.find((s) => s.mediaId === firstMediaId);
            if (firstSlide) firstSlide.slide.classList.add('is-active');
          }
        }
      }

      _removeGiftBoxSlides() {
        if (!this._giftBoxSlides) return;
        this._giftBoxSlides.forEach(({ slide, thumb }) => {
          slide.remove();
          if (thumb) thumb.remove();
        });
        this._giftBoxSlides = [];
      }

      _getActiveGiftBoxImages() {
        const type = this.selectedType;
        if (type === 'single') {
          return this._getSingleImages();
        }
        // While the modal is open the highlighted pair drives the gallery, so
        // the shopper sees the box before committing. Cleared on close, so the
        // cart property below can only ever read a confirmed pair.
        if (type === 'double' && this._previewPair) {
          return this._getDoubleImages(this._previewPair.companion, this._previewPair.primary);
        }
        if (type === 'double' && this._confirmedCompanion) {
          return this._getDoubleImages(this._confirmedCompanion.handle);
        }
        if (type === 'mini' && this._confirmedMini) {
          return this._imageConfig.mini ? [this._imageConfig.mini] : [];
        }
        return [];
      }

      _updateGalleryImage() {
        if (!this._giftBoxSlideInjected) return;

        const images = this._getActiveGiftBoxImages();
        const altMap = { single: 'Single Gift Box', double: 'Double Gift Box', mini: 'Mini Gift Box' };
        const alt = altMap[this.selectedType] || 'Gift Box';

        if (typeof this._mediaGallery.setActiveMedia !== 'function') {
          customElements.whenDefined('media-gallery').then(() => {
            this._updateGalleryImage();
          });
          return;
        }

        if (images.length > 0) {
          this._injectGiftBoxSlides(images, alt);
        } else {
          this._removeGiftBoxSlides();
          if (this._firstProductMediaId) {
            this._mediaGallery.setActiveMedia(this._firstProductMediaId, false);
          }
        }
      }

      /* ──────────── URL auto-select ──────────── */

      _checkUrlAutoSelect() {
        const params = new URLSearchParams(window.location.search);
        const autoType = params.get('gift_box');
        if (!autoType) return;

        const radio = this.querySelector(`.gift-box-picker__radio[value="${autoType}"]`);
        if (!radio) return;

        if (autoType === 'double' || autoType === 'mini') {
          this._revertType = this._currentType;
        }
        radio.checked = true;
        this._currentType = autoType;
        this._updatePanels();
        this._dispatchChange();

        if (autoType === 'double' && this.modal) {
          setTimeout(() => this._openModal(), 400);
        } else if (autoType === 'mini' && this.miniModal) {
          setTimeout(() => this._openMiniModal(), 400);
        }

        const url = new URL(window.location);
        url.searchParams.delete('gift_box');
        window.history.replaceState({}, '', url);
      }

      /* ──────────── Events ──────────── */

      _bindEvents() {
        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => {
            // Remember what was selected before switching to a modal-backed
            // type, so cancelling that modal can revert to it.
            if (radio.value === 'double' || radio.value === 'mini') {
              if (this._currentType !== radio.value) this._revertType = this._currentType;
            }
            this._currentType = radio.value;

            this._updatePanels();
            this._dispatchChange();

            if (radio.value === 'double' && this.modal) {
              this._openModal();
            }
            if (radio.value === 'mini' && this.miniModal) {
              this._openMiniModal();
            }
          });
        });

        const changeBtn = this.querySelector('[data-open-double-modal]');
        if (changeBtn) {
          changeBtn.addEventListener('click', () => this._openModal());
        }

        const changeMiniBtn = this.querySelector('[data-open-mini-modal]');
        if (changeMiniBtn) {
          changeMiniBtn.addEventListener('click', () => this._openMiniModal());
        }

        if (this.modal) {
          this.modal.querySelectorAll('[data-modal-close]').forEach((el) => {
            el.addEventListener('click', () => this._closeModal());
          });

          this.modal.addEventListener('keyup', (e) => {
            if (e.code === 'Escape') this._closeModal();
          });

          this.modal.querySelectorAll('.gift-box-combo__radio').forEach((radio) => {
            radio.addEventListener('change', () => this._onComboChange());
          });

          const confirmBtn = this.modal.querySelector('[data-modal-confirm]');
          if (confirmBtn) {
            confirmBtn.addEventListener('click', () => this._confirmModal());
          }
        }

        if (this.miniModal) {
          this.miniModal.querySelectorAll('[data-modal-close]').forEach((el) => {
            el.addEventListener('click', () => this._closeMiniModal());
          });

          this.miniModal.addEventListener('keyup', (e) => {
            if (e.code === 'Escape') this._closeMiniModal();
          });

          this.miniModal.querySelectorAll('.gift-box-picker__mini-btn').forEach((btn) => {
            btn.addEventListener('click', (e) => this._handleMiniStep(e));
          });

          const miniConfirmBtn = this.miniModal.querySelector('[data-mini-confirm]');
          if (miniConfirmBtn) {
            miniConfirmBtn.addEventListener('click', () => this._confirmMiniModal());
          }
        }
      }

      /* ──────────── Panels ──────────── */

      _updatePanels() {
        const type = this.selectedType;

        if (this.doubleSummary) {
          const showSummary = type === 'double' && this._confirmedCompanion;
          this.doubleSummary.toggleAttribute('hidden', !showSummary);
        }

        if (this.miniSummary) {
          const showMiniSummary = type === 'mini' && this._confirmedMini;
          this.miniSummary.toggleAttribute('hidden', !showMiniSummary);
        }

        this._updateQuantityVisibility();
        this._updateGalleryImage();
        this._updatePageReframe();
      }

      /* ──────────── Modal ──────────── */

      _openModal() {
        if (!this.modal) return;

        this._preselectCombo();
        this._updateModalConfirmState();
        this._updateModalPreviewFromSelections();
        this._previewGalleryFromSelection();
        this._updatePageReframe();

        this.modal.classList.add('is-opening');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.modal.classList.add('is-open');
            document.body.classList.add('overflow-hidden');
            const checked = this.modal.querySelector('.gift-box-combo__radio:checked');
            (checked || this.modal.querySelector('.gift-box-modal__content')).focus({
              preventScroll: true,
            });
          });
        });
      }

      /**
       * Nothing is ever left unselected: restore the confirmed pair, else the
       * mixed pair (one of each) — the box that shows off both expressions and
       * the one most shoppers want — else any pair with the product being
       * viewed, else the first pair. A pre-picked option is what keeps the
       * confirm button live from the moment the modal opens.
       */
      _preselectCombo() {
        const combos = Array.from(this.modal.querySelectorAll('.gift-box-combo__radio'));
        if (!combos.length) return;

        let target = null;

        if (this._confirmedPrimary && this._confirmedCompanion) {
          const want = [this._confirmedPrimary.handle, this._confirmedCompanion.handle].sort().join('|');
          target = combos.find(
            (r) => [r.dataset.primaryHandle, r.dataset.companionHandle].sort().join('|') === want
          );
        }

        const mixed = combos.filter((r) => r.dataset.comboMixed === 'true');

        if (!target) {
          target = mixed.find((r) => r.dataset.hasCurrent === 'true') || mixed[0];
        }

        if (!target) {
          target = combos.find((r) => r.dataset.hasCurrent === 'true');
        }

        if (!target) target = combos[0];

        target.checked = true;
      }

      _onComboChange() {
        this._updateModalConfirmState();
        this._updateModalPreviewFromSelections();
        this._previewGalleryFromSelection();
        this._updatePageReframe();
      }

      /** Point the product gallery at the highlighted (not yet confirmed) pair. */
      _previewGalleryFromSelection() {
        const combo = this._checkedCombo;
        if (!combo) return;

        this._previewPair = {
          primary: combo.dataset.primaryHandle,
          companion: combo.dataset.companionHandle,
        };
        this._updateGalleryImage();
      }

      _closeModal(confirmed) {
        if (!this.modal) return;
        this.modal.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');

        setTimeout(() => {
          this.modal.classList.remove('is-opening');
        }, 300);

        // Drop the preview either way — on confirm the stored pair takes over,
        // on cancel the gallery falls back to whatever was already chosen.
        this._previewPair = null;

        if (!confirmed) {
          if (!this._confirmedCompanion && this.selectedType === 'double') {
            this._revertToPreviousType();
          } else {
            // Snap the highlighted row back to the confirmed pair, so a
            // cancelled browse cannot leave the page describing a box the
            // shopper never chose.
            this._preselectCombo();
            this._updatePanels();
          }
        }
      }

      _revertToPreviousType() {
        const prevRadio =
          (this._revertType && this.querySelector(`.gift-box-picker__radio[value="${this._revertType}"]`)) ||
          this.querySelector('.gift-box-picker__radio[value="single"]') ||
          this.querySelector('.gift-box-picker__radio[value="none"]');
        if (prevRadio) {
          prevRadio.checked = true;
          this._currentType = prevRadio.value;
          this._updatePanels();
          this._dispatchChange();
        }
      }

      get _checkedCombo() {
        return this.modal ? this.modal.querySelector('.gift-box-combo__radio:checked') : null;
      }

      _updateModalConfirmState() {
        const combo = this._checkedCombo;
        const confirmBtn = this.modal.querySelector('[data-modal-confirm]');
        if (!confirmBtn) return;

        confirmBtn.disabled = !combo;

        const label = confirmBtn.querySelector('[data-confirm-label]');
        if (label) {
          // Name the pair on the button, and say "Choose" — this only sets the
          // selection, the separate add-to-cart button is what buys it.
          label.textContent = combo
            ? `Choose ${combo.dataset.comboLabel} — ${combo.dataset.comboPrice}`
            : 'Choose a pairing';
        }
      }

      _updateModalPreviewFromSelections() {
        const combo = this._checkedCombo;
        if (!combo) {
          this._updateModalPreview(null);
          return;
        }

        const pair = [combo.dataset.primaryHandle, combo.dataset.companionHandle].sort();
        let images = null;

        for (const cfg of this._imageConfig.double_configs || []) {
          const cfgPair = cfg.handles.split(',').map((h) => h.trim()).sort();
          if (cfgPair[0] === pair[0] && cfgPair[1] === pair[1]) {
            images = cfg.images || (cfg.image ? [cfg.image] : null);
            break;
          }
        }

        const firstImage = images ? images[0] : this._imageConfig.double_default;
        this._updateModalPreview(firstImage);
      }

      _updateModalPreview(imageUrl) {
        const previewEl = this.modal.querySelector('[data-modal-preview]');
        if (!previewEl) return;

        if (!imageUrl) {
          const defaultImg = this._imageConfig.double_default;
          if (previewEl.tagName === 'IMG' && defaultImg) {
            previewEl.src = defaultImg;
          }
          return;
        }

        if (previewEl.tagName === 'IMG') {
          previewEl.src = imageUrl;
        } else if (previewEl.tagName === 'DIV') {
          const img = document.createElement('img');
          img.className = 'gift-box-modal__thumb';
          img.src = imageUrl;
          img.alt = 'Double Gift Box';
          img.dataset.modalPreview = '';
          img.width = 72;
          previewEl.replaceWith(img);
        }
      }

      _confirmModal() {
        const combo = this._checkedCombo;
        if (!combo) return;

        this._confirmedPrimary = {
          variantId: combo.dataset.primaryId,
          title: combo.dataset.primaryTitle,
          handle: combo.dataset.primaryHandle,
          productUrl: combo.dataset.primaryUrl,
        };

        this._confirmedCompanion = {
          variantId: combo.dataset.companionId,
          title: combo.dataset.companionTitle,
          handle: combo.dataset.companionHandle,
        };

        this._confirmedCombo = {
          label: combo.dataset.comboLabel,
          cents: parseInt(combo.dataset.comboPriceCents, 10) || 0,
        };

        this._fillSummaryCard(combo);

        this._closeModal(true);
        this._updatePanels();
        this._dispatchChange();
        this._scrollToAddToCart();
      }

      /**
       * Choosing a pair is the halfway point, not the purchase — bring the
       * add-to-cart button into view so the next step is obvious. Runs on every
       * breakpoint: even on desktop, where the button sits beside the picker, it
       * can be below the fold once the confirmed pair expands the summary card.
       */
      _scrollToAddToCart() {
        const sectionId = this.dataset.sectionId;
        const button =
          (sectionId && document.getElementById(`ProductSubmitButton-${sectionId}`)) ||
          (sectionId && document.querySelector(`#MainProduct-${sectionId} .product-form__submit`)) ||
          document.querySelector('.product-form__submit');
        if (!button) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Wait out the close transition — body scroll is locked until then.
        setTimeout(() => {
          // Stop as soon as the button clears the bottom edge (plus a little
          // breathing room) rather than centring it, so the chosen pair above
          // it stays on screen.
          const gap = 24;
          const rect = button.getBoundingClientRect();
          const top = window.scrollY + rect.bottom - window.innerHeight + gap;

          window.scrollTo({
            top: Math.max(top, 0),
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }, 350);
      }

      /**
       * Mirror the chosen modal row onto the product page, so the confirmed
       * pair reads the same there as it did in the picker.
       */
      _fillSummaryCard(combo) {
        if (this.summaryNameEl) {
          this.summaryNameEl.textContent = combo.dataset.comboLabel;
        }

        const setImg = (el, src) => {
          if (!el) return;
          if (src) {
            el.src = src;
            el.hidden = false;
          } else {
            el.removeAttribute('src');
            el.hidden = true;
          }
        };

        setImg(this.querySelector('[data-summary-img-a]'), combo.dataset.primaryImg);
        setImg(this.querySelector('[data-summary-img-b]'), combo.dataset.companionImg);

        const detailEl = this.querySelector('[data-summary-detail]');
        if (detailEl) detailEl.textContent = combo.dataset.comboDetail || '';

        const priceEl = this.querySelector('[data-summary-price]');
        if (priceEl) priceEl.textContent = combo.dataset.comboPrice || '';

        const tagEl = this.querySelector('[data-summary-tag]');
        if (tagEl) {
          const isMixed = combo.dataset.comboMixed === 'true';
          tagEl.textContent = isMixed ? 'Mixed pair' : 'Same pair';
          tagEl.classList.toggle('gift-box-combo__tag--mixed', isMixed);
          tagEl.hidden = false;
        }
      }

      /* ──────────── Mini box ──────────── */

      /* ──────────── Mini modal ──────────── */

      _openMiniModal() {
        if (!this.miniModal) return;

        // Snapshot current quantities so closing without confirming can
        // restore them — the cart payload reads live input values, so they
        // must always match the last confirmed selection.
        this._miniSnapshot = Array.from(this.miniInputs).map((input) => input.value);

        this.miniModal.classList.add('is-opening');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.miniModal.classList.add('is-open');
            document.body.classList.add('overflow-hidden');
          });
        });
      }

      _closeMiniModal(confirmed) {
        if (!this.miniModal) return;
        this.miniModal.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');

        setTimeout(() => {
          this.miniModal.classList.remove('is-opening');
        }, 300);

        if (!confirmed) {
          if (this._miniSnapshot) {
            this.miniInputs.forEach((input, i) => {
              if (this._miniSnapshot[i] !== undefined) input.value = this._miniSnapshot[i];
            });
            this._updateMiniCount();
            this._updateMiniButtons();
          }

          if (!this._confirmedMini && this.selectedType === 'mini') {
            this._revertToPreviousType();
          }
        }
      }

      _confirmMiniModal() {
        if (this.miniTotal !== 4) return;

        const selections = [];
        this.miniInputs.forEach((input) => {
          const qty = parseInt(input.value, 10) || 0;
          if (qty > 0) {
            const title = input.dataset.companionTitle || 'Bottle';
            selections.push(`${qty}x ${title}`);
          }
        });

        this._confirmedMini = true;

        if (this.miniSummaryText) {
          this.miniSummaryText.textContent = selections.join(', ');
        }

        this._closeMiniModal(true);
        this._updatePanels();
        this._dispatchChange();
      }

      _handleMiniStep(event) {
        const btn = event.currentTarget;
        const row = btn.closest('.gift-box-modal__mini-row');
        const input = row.querySelector('.gift-box-picker__mini-qty');
        const currentVal = parseInt(input.value, 10) || 0;
        const action = btn.dataset.action;
        const total = this.miniTotal;

        if (action === 'plus' && total < 4) {
          input.value = Math.min(currentVal + 1, 4);
        } else if (action === 'minus' && currentVal > 0) {
          input.value = currentVal - 1;
        }

        this._updateMiniCount();
        this._updateMiniButtons();
      }

      _updateMiniCount() {
        if (!this.miniCountEl) return;
        const total = this.miniTotal;
        this.miniCountEl.textContent = total;

        if (total === 4) {
          this.miniCountEl.classList.add('is-complete');
        } else {
          this.miniCountEl.classList.remove('is-complete');
        }

        if (this.miniError) {
          this.miniError.toggleAttribute('hidden', total <= 4);
        }

        const miniConfirmBtn = this.miniModal?.querySelector('[data-mini-confirm]');
        if (miniConfirmBtn) {
          miniConfirmBtn.disabled = total !== 4;
        }
      }

      _updateMiniButtons() {
        const total = this.miniTotal;
        const container = this.miniModal || this;
        container.querySelectorAll('.gift-box-modal__mini-row').forEach((row) => {
          const input = row.querySelector('.gift-box-picker__mini-qty');
          const val = parseInt(input.value, 10) || 0;
          const minusBtn = row.querySelector('[data-action="minus"]');
          const plusBtn = row.querySelector('[data-action="plus"]');
          if (minusBtn) minusBtn.disabled = val <= 0;
          if (plusBtn) plusBtn.disabled = total >= 4;
        });
      }

      /* ──────────── Page reframing ──────────── */

      /**
       * Once a box is chosen the page is no longer selling the single bottle
       * it was opened on — it is selling a box that may not even contain that
       * bottle. Title and price are restated to match what the cart will
       * actually receive, and restored verbatim when the box is dropped.
       */
      _initPageReframe() {
        const sectionId = this.dataset.sectionId;
        const scope = document.getElementById(`MainProduct-${sectionId}`) || document;

        this._titleEls = Array.from(scope.querySelectorAll('.product__title h1, .product__title h2'));
        this._priceEl = document.getElementById(`price-${sectionId}`);

        this._originalTitles = this._titleEls.map((el) => el.textContent);
        this._originalPriceHTML = this._priceEl ? this._priceEl.innerHTML : null;
      }

      _boxTitle() {
        const type = this.selectedType;
        const short = this.dataset.currentShortTitle || '';

        if (type === 'single') return `Single Gift Box — ${short}`;
        if (type === 'mini') return 'Mini Gift Box';
        if (type === 'double') {
          const combo = this._checkedCombo;
          const label = combo
            ? combo.dataset.comboLabel
            : this._confirmedCombo && this._confirmedCombo.label;
          return label ? `Double Gift Box — ${label}` : 'Double Gift Box';
        }
        return null;
      }

      /** Total the shopper will actually be charged, in cents. */
      _bundleCents() {
        const type = this.selectedType;
        if (type === 'none') return null;

        const checked = this.querySelector('.gift-box-picker__radio:checked');
        const boxCents = checked ? parseInt(checked.dataset.giftPriceCents, 10) || 0 : 0;

        if (type === 'single') {
          const bottle = parseInt(this.dataset.currentPriceCents, 10) || 0;
          return boxCents + bottle;
        }

        if (type === 'double') {
          const combo = this._checkedCombo;
          const pairCents = combo
            ? parseInt(combo.dataset.comboPriceCents, 10) || 0
            : (this._confirmedCombo && this._confirmedCombo.cents) || 0;
          if (!pairCents) return null;
          return boxCents + pairCents;
        }

        if (type === 'mini') {
          if (!this._confirmedMini) return null;
          let bottles = 0;
          this.miniInputs.forEach((input) => {
            const qty = parseInt(input.value, 10) || 0;
            bottles += qty * (parseInt(input.dataset.priceCents, 10) || 0);
          });
          return boxCents + bottles;
        }

        return null;
      }

      _updatePageReframe() {
        if (!this._titleEls) return;

        const title = this._boxTitle();
        const cents = this._bundleCents();

        if (title) {
          this._titleEls.forEach((el) => {
            el.textContent = title;
          });
        } else {
          this._titleEls.forEach((el, i) => {
            el.textContent = this._originalTitles[i];
          });
        }

        if (!this._priceEl) return;

        if (cents === null) {
          if (this._originalPriceHTML !== null) this._priceEl.innerHTML = this._originalPriceHTML;
          return;
        }

        this._priceEl.innerHTML = `<div class="price price--large gift-box-price">
          <div class="price__container">
            <div class="price__regular">
              <span class="price-item price-item--regular">${this._formatMoney(cents)}</span>
            </div>
          </div>
        </div>`;
      }

      /** Mirrors Shopify's money filter against the shop's configured format. */
      _formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = (cents || 0) / 100;

        const withDelimiters = (decimals, thousands, decimalSep) => {
          const fixed = value.toFixed(decimals);
          const parts = fixed.split('.');
          const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts[1] ? whole + decimalSep + parts[1] : whole;
        };

        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, name) => {
          switch (name) {
            case 'amount_no_decimals':
              return withDelimiters(0, ',', '.');
            case 'amount_with_comma_separator':
              return withDelimiters(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withDelimiters(2, "'", '.');
            case 'amount_no_decimals_with_space_separator':
              return withDelimiters(0, ' ', ',');
            case 'amount_with_space_separator':
              return withDelimiters(2, ' ', ',');
            default:
              return withDelimiters(2, ',', '.');
          }
        });
      }

      /* ──────────── Helpers ──────────── */

      _generateGroupId() {
        return 'gb_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 8);
      }

      _dispatchChange() {
        this.dispatchEvent(new CustomEvent('gift-box-change', { bubbles: true }));
      }
    }
  );
}